import { useCallback } from 'react'

import { v4 as uuidv4 } from 'uuid'

import { useFileUploader } from '@tribeplatform/react-sdk/hooks'

import { useI18n } from '../../i18n/providers/I18nProvider.js'
import { handleUploadFileError } from '../../utils/error.js'
import { Attachment, FileWithID } from '../@types/index.js'
import { findNode } from '../helpers/node.js'

export const useAddAttachmentToEditor = ({ editor }) => {
  const intl = useI18n()
  const { uploadFile } = useFileUploader()

  const addAttachment = useCallback(
    async (file: FileWithID) => {
      const { name, size } = file
      const extension = name.slice(name.lastIndexOf('.') + 1).toLowerCase()

      const tempAttachmentData: Attachment = {
        id: uuidv4(),
        url: '',
        downloadUrl: '',
        size,
        extension,
        name,
        isLoading: true,
      }
      editor
        .chain()
        .focus()
        .setAttachment({
          attachment: tempAttachmentData,
          uploadId: file.id,
        })
        .setTextSelection(editor.state.selection.anchor) // to reset the selection
        .run()

      try {
        const uploadedFile = await uploadFile(file)

        const foundNode = findNode(
          editor.state,
          node => node?.attrs.attachment?.id === tempAttachmentData.id,
        )
        if (foundNode) {
          const { offset, node } = foundNode

          editor
            .chain()
            .setNodeSelection(offset)
            .focus()
            .setAttachment({
              attachment: uploadedFile,
              uploadId: undefined,
            })
            .setTextSelection(offset + node.nodeSize)
            .insertContent(' ')
            .run()
        }
      } catch (error) {
        handleUploadFileError(intl)(error)

        if (tempAttachmentData.id) {
          const foundNode = findNode(
            editor.state,
            node => node?.attrs.attachment?.id === tempAttachmentData.id,
          )
          if (foundNode) {
            const { offset } = foundNode
            // Delete the actual node (image/video tag) from the DOM
            editor.commands.deleteRange({
              from: offset,
              to: offset + 1,
            })
          }
        }
      }
    },
    [editor, uploadFile, intl],
  )

  return {
    addAttachment,
  }
}
